import React, { useState, useRef, useEffect } from "react";


import { DragDropContext, Droppable, Draggable,  } from "@hello-pangea/dnd";
import { useAuth } from "../context/AuthContext";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  IconButton,
} from "@mui/material";

import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";

const Dashboard = () => {
    // --- Drag offset logic for System Logic drag ---
    const [dragOffset, setDragOffset] = React.useState(0);
    // Capture offset when drag starts
    const handleSystemDragStart = React.useCallback((e) => {
      const target = e.target.closest('li');
      if (target && e.clientY) {
        setDragOffset(e.clientY - target.getBoundingClientRect().top);
      }
    }, []);
    // Reset offset on drag end
    const handleSystemDragEnd = (result) => {
      setDragOffset(0);
      handleDragEnd(result);
    };
  useForceUpdateEverySecond();
  // Add LED flash animation CSS once
  useEffect(() => {
    if (!document.getElementById('led-flash-style')) {
      const ledFlashStyle = document.createElement('style');
      ledFlashStyle.id = 'led-flash-style';
      ledFlashStyle.innerHTML = `@keyframes led-flash { 0% { opacity: 1; } 100% { opacity: 0.3; } }`;
      document.head.appendChild(ledFlashStyle);
    }
  }, []);
  // Section visibility states
  const [showSystem, setShowSystem] = useState(true);
  const [showRules, setShowRules] = useState(true);
  const [showAllowedTopics, setShowAllowedTopics] = useState(true);
  const [showRejectionMessages, setShowRejectionMessages] = useState(true);
  const [showExtractionPrompts, setShowExtractionPrompts] = useState(true);

  // Helper for structured rules (AI logic builder blocks)
  const [ruleBlocks, setRuleBlocks] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_rule_blocks");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Extraction prompts state
  const [extractionPrompts, setExtractionPrompts] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_extraction_prompts");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [newExtractionPrompt, setNewExtractionPrompt] = useState("");
  const [editExtractionIdx, setEditExtractionIdx] = useState(null);
  const [editExtractionText, setEditExtractionText] = useState("");

  // Custom hook to force re-render every second for real-time LED
  function useForceUpdateEverySecond() {
    const [, setTick] = useState(0);
    useEffect(() => {
      const interval = setInterval(() => setTick(t => t + 1), 1000);
      return () => clearInterval(interval);
    }, []);
  }

  // Track mouse Y position globally for drag-follow effect
  let __dragMouseY = 0;
  if (typeof window !== 'undefined') {
    window.addEventListener('mousemove', (e) => {
      __dragMouseY = e.clientY;
      //console.log('Mouse Y:', __dragMouseY);
    });
  }

  // Extraction prompts handlers
  const handleAddExtractionPrompt = () => {
    if (newExtractionPrompt.trim()) {
      setExtractionPrompts((prev) => {
        const updated = [...prev, newExtractionPrompt];
        localStorage.setItem("cb_ai_extraction_prompts", JSON.stringify(updated));
        return updated;
      });
      setNewExtractionPrompt("");
    }
  };
  const handleEditExtractionPrompt = (idx) => {
    setEditExtractionIdx(idx);
    setEditExtractionText(extractionPrompts[idx]);
  };
  const handleSaveExtractionPrompt = () => {
    if (editExtractionText.trim()) {
      setExtractionPrompts((prev) => {
        const updated = prev.map((r, i) =>
          i === editExtractionIdx ? editExtractionText : r
        );
        localStorage.setItem("cb_ai_extraction_prompts", JSON.stringify(updated));
        return updated;
      });
      setEditExtractionIdx(null);
      setEditExtractionText("");
    }
  };
  const handleRemoveExtractionPrompt = (idx) => {
    setExtractionPrompts((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      localStorage.setItem("cb_ai_extraction_prompts", JSON.stringify(updated));
      return updated;
    });
    if (editExtractionIdx === idx) {
      setEditExtractionIdx(null);
      setEditExtractionText("");
    }
  };

  // Rejection messages state
  const [rejectionMessages, setRejectionMessages] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_rejection_messages");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [newRejectionMessage, setNewRejectionMessage] = useState("");
  const [editRejectionIdx, setEditRejectionIdx] = useState(null);
  const [editRejectionText, setEditRejectionText] = useState("");

  // Rejection messages handlers
  const handleAddRejectionMessage = () => {
    if (newRejectionMessage.trim()) {
      setRejectionMessages((prev) => {
        const updated = [...prev, newRejectionMessage];
        localStorage.setItem("cb_ai_rejection_messages", JSON.stringify(updated));
        return updated;
      });
      setNewRejectionMessage("");
    }
  };
  const handleEditRejectionMessage = (idx) => {
    setEditRejectionIdx(idx);
    setEditRejectionText(rejectionMessages[idx]);
  };
  const handleSaveRejectionMessage = () => {
    if (editRejectionText.trim()) {
      setRejectionMessages((prev) => {
        const updated = prev.map((r, i) =>
          i === editRejectionIdx ? editRejectionText : r
        );
        localStorage.setItem("cb_ai_rejection_messages", JSON.stringify(updated));
        return updated;
      });
      setEditRejectionIdx(null);
      setEditRejectionText("");
    }
  };
  const handleRemoveRejectionMessage = (idx) => {
    setRejectionMessages((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      localStorage.setItem("cb_ai_rejection_messages", JSON.stringify(updated));
      return updated;
    });
    if (editRejectionIdx === idx) {
      setEditRejectionIdx(null);
      setEditRejectionText("");
    }
  };

  // Allowed topics state
  const [allowedTopics, setAllowedTopics] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_allowed_topics");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [newAllowedTopic, setNewAllowedTopic] = useState("");
  const [editAllowedTopicIdx, setEditAllowedTopicIdx] = useState(null);
  const [editAllowedTopicText, setEditAllowedTopicText] = useState("");

  // Allowed topics handlers
  const handleAddAllowedTopic = () => {
    if (newAllowedTopic.trim()) {
      setAllowedTopics((prev) => {
        const updated = [...prev, newAllowedTopic];
        localStorage.setItem("cb_ai_allowed_topics", JSON.stringify(updated));
        return updated;
      });
      setNewAllowedTopic("");
    }
  };
  const handleEditAllowedTopic = (idx) => {
    setEditAllowedTopicIdx(idx);
    setEditAllowedTopicText(allowedTopics[idx]);
  };
  const handleSaveAllowedTopic = () => {
    if (editAllowedTopicText.trim()) {
      setAllowedTopics((prev) => {
        const updated = prev.map((r, i) =>
          i === editAllowedTopicIdx ? editAllowedTopicText : r
        );
        localStorage.setItem("cb_ai_allowed_topics", JSON.stringify(updated));
        return updated;
      });
      setEditAllowedTopicIdx(null);
      setEditAllowedTopicText("");
    }
  };
  const handleRemoveAllowedTopic = (idx) => {
    setAllowedTopics((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      localStorage.setItem("cb_ai_allowed_topics", JSON.stringify(updated));
      return updated;
    });
    if (editAllowedTopicIdx === idx) {
      setEditAllowedTopicIdx(null);
      setEditAllowedTopicText("");
    }
  };

  // System logic state
  const [systemLogic, setSystemLogic] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_system");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [newSystemLogic, setNewSystemLogic] = useState("");
  const [editSystemIdx, setEditSystemIdx] = useState(null);
  const [editSystemText, setEditSystemText] = useState("");

  // System logic handlers
  const handleAddSystemLogic = () => {
    if (newSystemLogic.trim()) {
      setSystemLogic((prev) => {
        const updated = [...prev, newSystemLogic];
        localStorage.setItem("cb_ai_system", JSON.stringify(updated));
        return updated;
      });
      setNewSystemLogic("");
    }
  };
  const handleEditSystemLogic = (idx) => {
    setEditSystemIdx(idx);
    setEditSystemText(systemLogic[idx]);
  };
  const handleSaveSystemLogic = () => {
    if (editSystemText.trim()) {
      setSystemLogic((prev) => {
        const updated = prev.map((r, i) =>
          i === editSystemIdx ? editSystemText : r
        );
        localStorage.setItem("cb_ai_system", JSON.stringify(updated));
        return updated;
      });
      setEditSystemIdx(null);
      setEditSystemText("");
    }
  };
  const handleRemoveSystemLogic = (idx) => {
    setSystemLogic((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      localStorage.setItem("cb_ai_system", JSON.stringify(updated));
      return updated;
    });
    if (editSystemIdx === idx) {
      setEditSystemIdx(null);
      setEditSystemText("");
    }
  };

  // Rules state for AI
  const [rules, setRules] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_rules");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [newRule, setNewRule] = useState("");
  const [editRuleIdx, setEditRuleIdx] = useState(null);
  const [editRuleText, setEditRuleText] = useState("");

  const handleAddRule = () => {
    if (newRule.trim()) {
      setRules((prev) => {
        const updated = [...prev, newRule];
        localStorage.setItem("cb_ai_rules", JSON.stringify(updated));
        return updated;
      });
      setNewRule("");
    }
  };
  const handleEditRule = (idx) => {
    setEditRuleIdx(idx);
    setEditRuleText(rules[idx]);
  };
  const handleSaveRule = () => {
    if (editRuleText.trim()) {
      setRules((prev) => {
        const updated = prev.map((r, i) =>
          i === editRuleIdx ? editRuleText : r
        );
        localStorage.setItem("cb_ai_rules", JSON.stringify(updated));
        return updated;
      });
      setEditRuleIdx(null);
      setEditRuleText("");
    }
  };
  const handleRemoveRule = (idx) => {
    setRules((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      localStorage.setItem("cb_ai_rules", JSON.stringify(updated));
      return updated;
    });
    if (editRuleIdx === idx) {
      setEditRuleIdx(null);
      setEditRuleText("");
    }
  };

  // ========= DND: unified handler =========

  const getListConfig = (id) => {
    switch (id) {
      case "systemLogic":
        return {
          list: systemLogic,
          setList: setSystemLogic,
          storageKey: "cb_ai_system",
        };
      case "allowedTopics":
        return {
          list: allowedTopics,
          setList: setAllowedTopics,
          storageKey: "cb_ai_allowed_topics",
        };
      case "rejectionMessages":
        return {
          list: rejectionMessages,
          setList: setRejectionMessages,
          storageKey: "cb_ai_rejection_messages",
        };
      case "extractionPrompts":
        return {
          list: extractionPrompts,
          setList: setExtractionPrompts,
          storageKey: "cb_ai_extraction_prompts",
        };
      case "rules":
        return {
          list: rules,
          setList: setRules,
          storageKey: "cb_ai_rules",
        };
      case "buildingBlock":
        return {
          list: ruleBlocks,
          setList: setRuleBlocks,
          storageKey: null,
        };
      default:
        return null;
    }
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceCfg = getListConfig(source.droppableId);
    const destCfg = getListConfig(destination.droppableId);
    if (!sourceCfg || !destCfg) return;

    // Reorder within same list
    if (source.droppableId === destination.droppableId) {
      const items = Array.from(sourceCfg.list);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      sourceCfg.setList(items);
      if (sourceCfg.storageKey) {
        localStorage.setItem(sourceCfg.storageKey, JSON.stringify(items));
      }
      return;
    }

    // Only allow dropping INTO buildingBlock from other lists
    if (destination.droppableId !== "buildingBlock") {
      // ignore drops between non-buildingBlock lists
      return;
    }

    // Copy item to buildingBlock, do not remove from source
    let reference = "";
    switch (source.droppableId) {
      case "systemLogic": reference = "[System] "; break;
      case "rules": reference = "[Rule] "; break;
      case "extractionPrompts": reference = "[Prompt] "; break;
      case "allowedTopics": reference = "[Topic] "; break;
      case "rejectionMessages": reference = "[Rejection] "; break;
      default: reference = "";
    }
    const moved = reference + sourceCfg.list[source.index];
    const destItems = Array.from(destCfg.list);
    destItems.splice(destination.index, 0, moved);
    destCfg.setList(destItems);
    // Always update localStorage for ruleBlocks (Building Block)
    localStorage.setItem("cb_ai_rule_blocks", JSON.stringify(destItems));
    // Mirror Building Block in Ask a question field
    // Sort Building Block items by reference type before mirroring
    const order = [
      '[Prompt]',
      '[Topic]',
      '[System]',
      '[Rule]',
      '[Rejection]'
    ];
    const sorted = [...destItems].sort((a, b) => {
      const getType = (str) => order.findIndex(type => str.startsWith(type));
      return getType(a) - getType(b);
    });
    // Remove prepended [Reference] part for ask area
    const clean = sorted.map(str => str.replace(/^\[[^\]]+\]\s*/, ""));
    const askArr = clean.join('; ');
    setAskText(askArr);
    localStorage.setItem("cb_ai_ask_array", JSON.stringify(askArr.split(';').map(s => s.trim()).filter(Boolean)));
  };

  // ==================== MQTT + AI base state ====================

  var { token, user } = useAuth();

  const [devices, setDevices] = useState(null);
  const [topics, setTopics] = useState(null);
  const [readings, setReadings] = useState(null);
  const [error, setError] = useState("");

  // AI & LLM state
  const [aiVisible, setAiVisible] = useState(false);
  const [askText, setAskText] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_ask_array");
      if (stored) {
        const arr = JSON.parse(stored);
        if (Array.isArray(arr)) return arr.join('; ');
      }
    } catch {}
    return "";
  });
  const [askArray, setAskArray] = useState(() => {
    try {
      const stored = localStorage.getItem("cb_ai_prompts");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [aiResponse, setAiResponse] = useState("");

  // interval handles for auto-refresh
  const devicesIntervalRef = useRef(null);
  const readingsIntervalRef = useRef(null);
  const topicsIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (devicesIntervalRef.current) clearInterval(devicesIntervalRef.current);
      if (readingsIntervalRef.current) clearInterval(readingsIntervalRef.current);
      if (topicsIntervalRef.current) clearInterval(topicsIntervalRef.current);
    };
  }, []);

  const pageStyles = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #181c2a 0%, #23263a 100%)",
    color: "#e3e6f0",
    fontFamily: "Inter, Segoe UI, Arial, sans-serif",
    paddingBottom: "40px",
  };

  const buttonStyle = {
    fontWeight: 600,
    borderRadius: 2,
    fontSize: 16,
    px: 3,
    py: 1.2,
    boxShadow: "none",
    textTransform: "none",
    bgcolor: "#818cf8",
    color: "#fff",
    ":hover": { bgcolor: "#6366f1" },
    transition: "background 0.2s",
  };

  const fetchDevices = async () => {
    setError("");
    setDevices(null);
    try {
      //const res = await fetch("https://serenity.ing/mqtt/dashboard/devices", {
      //  headers: {
      //    Authorization: `Bearer ${token}`,
      //  },
      //});
      const res = await fetch("https://serenity.ing/mqtt/dashboard/devices");
      if (!res.ok) throw new Error("Failed to fetch devices");
      const data = await res.json();
      setDevices(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchTopics = async () => {
    setError("");
    setTopics(null);
    try {
      const res = await fetch("https://serenity.ing/mqtt/dashboard/topics");
      if (!res.ok) throw new Error("Failed to fetch MQTT topics");
      const data = await res.json();
      setTopics(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchReadings = async () => {
    setError("");
    setReadings(null);
    try {
      const res = await fetch("https://serenity.ing/mqtt/dashboard/devices/latest");
      if (!res.ok) throw new Error("Failed to fetch latest readings");
      const data = await res.json();
      const cleaned = {
        ...data,
        latest_readings: Array.isArray(data.latest_readings)
          ? data.latest_readings.map(({ payload, topic, ...rest }) => rest)
          : [],
      };
      setReadings(cleaned);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFetchDevicesClick = () => {
    fetchDevices();
    if (devicesIntervalRef.current) clearInterval(devicesIntervalRef.current);
    devicesIntervalRef.current = setInterval(fetchDevices, 90000);
  };

  const handleFetchReadingsClick = () => {
    fetchReadings();
    if (readingsIntervalRef.current)
      clearInterval(readingsIntervalRef.current);
    readingsIntervalRef.current = setInterval(fetchReadings, 90000);
  };

  const handleFetchTopicsClick = () => {
    fetchTopics();
    if (topicsIntervalRef.current) clearInterval(topicsIntervalRef.current);
    topicsIntervalRef.current = setInterval(fetchTopics, 90000);
  };

  const handleAiClick = () => {
    setAiVisible((v) => !v);
  };

  const handleAsk = () => {
    if (askText.trim()) {
      setAskArray((prev) => {
        const updated = [...prev, askText];
        localStorage.setItem("cb_ai_prompts", JSON.stringify(updated));
        return updated;
      });
      setAiResponse("Thinking...");
      setTimeout(() => {
        setAiResponse(
          `Response to: "${askText}"\nRules: ${
            rules.length ? rules.join(" | ") : "None"
          }`
        );
      }, 1200);
      setAskText("");
    }
  };

  const handleRemovePrompt = (idx) => {
    setAskArray((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      localStorage.setItem("cb_ai_prompts", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <div style={pageStyles}>
      <Paper
        sx={{
          p: 3,
          mt: 0,
          mb: 2,
          borderRadius: 3,
          boxShadow: 1,
          width: "80%",
          mx: "auto",
          background: "#23263a",
          color: "#e3e6f0",
        }}
      >
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: 700, color: "#fff" }}
        >
          Welcome, {user?.email || "Admin"}
        </Typography>
        <Button
          variant="contained"
          onClick={handleFetchDevicesClick}
          sx={{ ...buttonStyle, mb: 2, mr: 2 }}
        >
          Fetch Devices
        </Button>
        <Button
          variant="contained"
          onClick={handleFetchReadingsClick}
          sx={{ ...buttonStyle, mb: 2, mr: 2 }}
        >
          Fetch Readings
        </Button>
        <Button
          variant="contained"
          onClick={handleFetchTopicsClick}
          sx={{ ...buttonStyle, mb: 2, mr: 2 }}
        >
          Fetch Topics
        </Button>
        <Button
          variant="contained"
          onClick={handleAiClick}
          sx={{ ...buttonStyle, mb: 2 }}
        >
          AI & LLM
        </Button>
      </Paper>

      {aiVisible && (
        <Paper
          className="glass-card glow"
          sx={{
            p: 3,
            mt: 2,
            mb: 2,
            borderRadius: 3,
            boxShadow: 1,
            width: "80%",
            mx: "auto",
            background: "#23263a",
            color: "#e3e6f0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 700, color: "#fff" }}
          >
            AI & LLM Assistant
          </Typography>

          <DragDropContext onDragEnd={handleSystemDragEnd}>
            {/* System Logic */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  System Logic:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowSystem((v) => !v)}
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  {showSystem ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>
              {showSystem && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <input
                      type="text"
                      value={newSystemLogic}
                      onChange={(e) => setNewSystemLogic(e.target.value)}
                      placeholder="Add new system logic..."
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #818cf8",
                        background: "#181c2a",
                        color: "#e3e6f0",
                        fontSize: 16,
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddSystemLogic}
                      sx={buttonStyle}
                    >
                      Add
                    </Button>
                  </div>
                  <Droppable droppableId="systemLogic" direction="vertical">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          resize: "vertical",
                          overflow: "auto",
                          minHeight: 48,
                          maxHeight: 240,
                          background: "#181c2a",
                          borderRadius: 8,
                          border: "1px solid #818cf8",
                          width: "100%",
                          padding: "8px 0",
                          marginBottom: 8,
                        }}
                      >
                        <ul style={{ margin: 0, padding: 0, listStyle: "disc" }}>
                          {systemLogic.map((logic, idx) => (
                            <Draggable
                              key={`system-${idx}`}
                              draggableId={`system-${idx}`}
                              index={idx}
                            >
                              {(providedDraggable) => (
                                <li
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                  onMouseDown={handleSystemDragStart}
                                  style={{
                                    ...providedDraggable.draggableProps.style,
                                    color: "#e3e6f0",
                                    marginBottom: 4,
                                    paddingLeft: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    cursor: "grab",
                                    ...(providedDraggable.snapshot && providedDraggable.snapshot.isDragging
                                      ? {
                                          position: 'fixed',
                                          left: providedDraggable.draggableProps.style?.left || 0,
                                          top: __dragMouseY,
                                          width: providedDraggable.draggableProps.style?.width || 'auto',
                                          zIndex: 9999,
                                        }
                                      : {}),
                                  }}
                                >
                                  {editSystemIdx === idx ? (
                                    <>
                                      <input
                                        type="text"
                                        value={editSystemText}
                                        onChange={(e) =>
                                          setEditSystemText(e.target.value)
                                        }
                                        style={{
                                          flex: 1,
                                          padding: "4px 8px",
                                          borderRadius: 6,
                                          border: "1px solid #818cf8",
                                          background: "#23263a",
                                          color: "#e3e6f0",
                                          fontSize: 15,
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        onClick={handleSaveSystemLogic}
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() => setEditSystemIdx(null)}
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#23263a",
                                          ":hover": { bgcolor: "#181c2a" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ flex: 1 }}>{logic}</span>
                                      <Button
                                        size="small"
                                        onClick={() => handleEditSystemLogic(idx)}
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleRemoveSystemLogic(idx)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#be185d",
                                          ":hover": { bgcolor: "#a21caf" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  )}
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      </div>
                    )}
                  </Droppable>
                </>
              )}
            </div>

            {/* Rules */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  Rules:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowRules((v) => !v)}
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  {showRules ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>
              {showRules && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <input
                      type="text"
                      value={newRule}
                      onChange={(e) => setNewRule(e.target.value)}
                      placeholder="Add new rule..."
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #818cf8",
                        background: "#181c2a",
                        color: "#e3e6f0",
                        fontSize: 16,
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddRule}
                      sx={buttonStyle}
                    >
                      Add
                    </Button>
                  </div>
                  <Droppable droppableId="rules" direction="vertical">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          resize: "vertical",
                          overflow: "auto",
                          minHeight: 48,
                          maxHeight: 240,
                          background: "#181c2a",
                          borderRadius: 8,
                          border: "1px solid #818cf8",
                          width: "100%",
                          padding: "8px 0",
                          marginBottom: 8,
                        }}
                      >
                        <ul style={{ margin: 0, padding: 0, listStyle: "disc" }}>
                          {rules.map((rule, idx) => (
                            <Draggable
                              key={`rule-${idx}`}
                              draggableId={`rule-${idx}`}
                              index={idx}
                            >
                              {(providedDraggable) => (
                                <li
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                  style={{
                                    ...providedDraggable.draggableProps.style,
                                    color: "#e3e6f0",
                                    marginBottom: 4,
                                    paddingLeft: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    cursor: "grab",
                                  }}
                                >
                                  {editRuleIdx === idx ? (
                                    <>
                                      <input
                                        type="text"
                                        value={editRuleText}
                                        onChange={(e) =>
                                          setEditRuleText(e.target.value)
                                        }
                                        style={{
                                          flex: 1,
                                          padding: "4px 8px",
                                          borderRadius: 6,
                                          border: "1px solid #818cf8",
                                          background: "#23263a",
                                          color: "#e3e6f0",
                                          fontSize: 15,
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        onClick={handleSaveRule}
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() => setEditRuleIdx(null)}
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#23263a",
                                          ":hover": { bgcolor: "#181c2a" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ flex: 1 }}>{rule}</span>
                                      <Button
                                        size="small"
                                        onClick={() => handleEditRule(idx)}
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() => handleRemoveRule(idx)}
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#be185d",
                                          ":hover": { bgcolor: "#a21caf" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  )}
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      </div>
                    )}
                  </Droppable>
                </>
              )}
            </div>

            {/* Extraction Prompts */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  Extraction Prompts:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowExtractionPrompts((v) => !v)}
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  {showExtractionPrompts ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>
              {showExtractionPrompts && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <input
                      type="text"
                      value={newExtractionPrompt}
                      onChange={(e) => setNewExtractionPrompt(e.target.value)}
                      placeholder="Add extraction prompt..."
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #818cf8",
                        background: "#181c2a",
                        color: "#e3e6f0",
                        fontSize: 16,
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddExtractionPrompt}
                      sx={buttonStyle}
                    >
                      Add
                    </Button>
                  </div>
                  <Droppable
                    droppableId="extractionPrompts"
                    direction="vertical"
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          resize: "vertical",
                          overflow: "auto",
                          minHeight: 48,
                          maxHeight: 240,
                          background: "#181c2a",
                          borderRadius: 8,
                          border: "1px solid #818cf8",
                          width: "100%",
                          padding: "8px 0",
                          marginBottom: 8,
                        }}
                      >
                        <ul style={{ margin: 0, padding: 0, listStyle: "disc" }}>
                          {extractionPrompts.map((prompt, idx) => (
                            <Draggable
                              key={`extraction-${idx}`}
                              draggableId={`extraction-${idx}`}
                              index={idx}
                            >
                              {(providedDraggable) => (
                                <li
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                  style={{
                                    ...providedDraggable.draggableProps.style,
                                    color: "#e3e6f0",
                                    marginBottom: 4,
                                    paddingLeft: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    cursor: "grab",
                                  }}
                                >
                                  {editExtractionIdx === idx ? (
                                    <>
                                      <input
                                        type="text"
                                        value={editExtractionText}
                                        onChange={(e) =>
                                          setEditExtractionText(e.target.value)
                                        }
                                        style={{
                                          flex: 1,
                                          padding: "4px 8px",
                                          borderRadius: 6,
                                          border: "1px solid #818cf8",
                                          background: "#23263a",
                                          color: "#e3e6f0",
                                          fontSize: 15,
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        onClick={handleSaveExtractionPrompt}
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          setEditExtractionIdx(null)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#23263a",
                                          ":hover": { bgcolor: "#181c2a" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ flex: 1 }}>{prompt}</span>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleEditExtractionPrompt(idx)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleRemoveExtractionPrompt(idx)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#be185d",
                                          ":hover": { bgcolor: "#a21caf" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  )}
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      </div>
                    )}
                  </Droppable>
                </>
              )}
            </div>

            {/* Allowed Topics */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  Allowed Topics:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowAllowedTopics((v) => !v)}
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  {showAllowedTopics ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>
              {showAllowedTopics && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <input
                      type="text"
                      value={newAllowedTopic}
                      onChange={(e) => setNewAllowedTopic(e.target.value)}
                      placeholder="Add allowed topic..."
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #818cf8",
                        background: "#181c2a",
                        color: "#e3e6f0",
                        fontSize: 16,
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddAllowedTopic}
                      sx={buttonStyle}
                    >
                      Add
                    </Button>
                  </div>
                  <Droppable droppableId="allowedTopics" direction="vertical">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          resize: "vertical",
                          overflow: "auto",
                          minHeight: 48,
                          maxHeight: 240,
                          background: "#181c2a",
                          borderRadius: 8,
                          border: "1px solid #818cf8",
                          width: "100%",
                          padding: "8px 0",
                          marginBottom: 8,
                        }}
                      >
                        <ul style={{ margin: 0, padding: 0, listStyle: "disc" }}>
                          {allowedTopics.map((topic, idx) => (
                            <Draggable
                              key={`allowed-${idx}`}
                              draggableId={`allowed-${idx}`}
                              index={idx}
                            >
                              {(providedDraggable) => (
                                <li
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                  style={{
                                    ...providedDraggable.draggableProps.style,
                                    color: "#e3e6f0",
                                    marginBottom: 4,
                                    paddingLeft: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    cursor: "grab",
                                  }}
                                >
                                  {editAllowedTopicIdx === idx ? (
                                    <>
                                      <input
                                        type="text"
                                        value={editAllowedTopicText}
                                        onChange={(e) =>
                                          setEditAllowedTopicText(e.target.value)
                                        }
                                        style={{
                                          flex: 1,
                                          padding: "4px 8px",
                                          borderRadius: 6,
                                          border: "1px solid #818cf8",
                                          background: "#23263a",
                                          color: "#e3e6f0",
                                          fontSize: 15,
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        onClick={handleSaveAllowedTopic}
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          setEditAllowedTopicIdx(null)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#23263a",
                                          ":hover": { bgcolor: "#181c2a" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ flex: 1 }}>{topic}</span>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleEditAllowedTopic(idx)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleRemoveAllowedTopic(idx)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#be185d",
                                          ":hover": { bgcolor: "#a21caf" },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  )}
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      </div>
                    )}
                  </Droppable>
                </>
              )}
            </div>

            {/* Rejection Messages */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  Rejection Messages:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowRejectionMessages((v) => !v)}
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  {showRejectionMessages ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>
              {showRejectionMessages && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <input
                      type="text"
                      value={newRejectionMessage}
                      onChange={(e) => setNewRejectionMessage(e.target.value)}
                      placeholder="Add rejection message..."
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #818cf8",
                        background: "#181c2a",
                        color: "#e3e6f0",
                        fontSize: 16,
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddRejectionMessage}
                      sx={buttonStyle}
                    >
                      Add
                    </Button>
                  </div>
                  <Droppable
                    droppableId="rejectionMessages"
                    direction="vertical"
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          resize: "vertical",
                          overflow: "auto",
                          minHeight: 48,
                          maxHeight: 240,
                          background: "#181c2a",
                          borderRadius: 8,
                          border: "1px solid #818cf8",
                          width: "100%",
                          padding: "8px 0",
                          marginBottom: 8,
                        }}
                      >
                        <ul style={{ margin: 0, padding: 0, listStyle: "disc" }}>
                          {rejectionMessages.map((msg, idx) => (
                            <Draggable
                              key={`rejection-${idx}`}
                              draggableId={`rejection-${idx}`}
                              index={idx}
                            >
                              {(providedDraggable) => (
                                <li
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                  style={{
                                    ...providedDraggable.draggableProps.style,
                                    color: "#e3e6f0",
                                    marginBottom: 4,
                                    paddingLeft: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    cursor: "grab",
                                  }}
                                >
                                  {editRejectionIdx === idx ? (
                                    <>
                                      <input
                                        type="text"
                                        value={editRejectionText}
                                        onChange={(e) =>
                                          setEditRejectionText(e.target.value)
                                        }
                                        style={{
                                          flex: 1,
                                          padding: "4px 8px",
                                          borderRadius: 6,
                                          border:
                                            "1px solid #818cf8",
                                          background: "#23263a",
                                          color: "#e3e6f0",
                                          fontSize: 15,
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        onClick={handleSaveRejectionMessage}
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          setEditRejectionIdx(null)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#23263a",
                                          ":hover": {
                                            bgcolor: "#181c2a",
                                          },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ flex: 1 }}>{msg}</span>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleEditRejectionMessage(idx)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleRemoveRejectionMessage(idx)
                                        }
                                        sx={{
                                          ...buttonStyle,
                                          bgcolor: "#be185d",
                                          ":hover": {
                                            bgcolor: "#a21caf",
                                          },
                                          minWidth: 0,
                                          px: 2,
                                          fontSize: 14,
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  )}
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      </div>
                    )}
                  </Droppable>
                </>
              )}
            </div>

            {/* Building Block droppable target */}
            <Typography
              variant="subtitle2"
              sx={{ color: "#818cf8", mb: 1, mt: 4 }}
            >
              Building Block:
            </Typography>
            <Droppable droppableId="buildingBlock" direction="vertical">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    minHeight: 60,
                    background: snapshot.isDraggingOver
                      ? "#23263a"
                      : "#181c2a",
                    border: "2px dashed #818cf8",
                    borderRadius: 8,
                    padding: "16px 8px",
                    marginTop: 8,
                    marginBottom: 24,
                  }}
                >
                  {ruleBlocks.length === 0 ? (
                    <Typography sx={{ color: "#818cf8", opacity: 0.7 }}>
                      Drag items here from System, Rules, Topics, Rejections or
                      Extraction to build a final query.
                    </Typography>
                  ) : (
                    <ul
                      style={{
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                      }}
                    >
                      {ruleBlocks.map((block, idx) => (
                        <li
                          key={`block-${idx}`}
                          style={{
                            color: "#e3e6f0",
                            marginBottom: 8,
                            padding: "6px 10px",
                            borderRadius: 6,
                            background: "#23263a",
                            border: "1px solid #4b5563",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{block}</span>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Remove block"
                            onClick={() => {
                              setRuleBlocks((prev) => {
                                const updated = prev.filter((_, i) => i !== idx);
                                localStorage.setItem("cb_ai_rule_blocks", JSON.stringify(updated));
                                // Group items by reference type
                                const order = [
                                  '[Extraction Prompt]',
                                  '[Topic]',
                                  '[System]',
                                  '[Rule]',
                                  '[Rejection]'
                                ];
                                // Create a map: type -> items
                                const typeMap = {};
                                for (const type of order) typeMap[type] = [];
                                for (const str of updated) {
                                  const type = order.find(t => str.startsWith(t));
                                  if (type) typeMap[type].push(str.replace(/^\[[^\]]+\]\s*/, ""));
                                }
                                // Build askText: if multiple items from same type, put in [item1,item2,...]
                                const askParts = [];
                                for (const type of order) {
                                  if (typeMap[type].length === 1) {
                                    askParts.push(typeMap[type][0]);
                                  } else if (typeMap[type].length > 1) {
                                    askParts.push(`[${typeMap[type].join(',')}]`);
                                  }
                                }
                                const askArr = askParts.join('; ');
                                setAskText(askArr);
                                localStorage.setItem("cb_ai_ask_array", JSON.stringify(askArr.split(';').map(s => s.trim()).filter(Boolean)));
                                return updated;
                              });
                            }}
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </li>
                      ))}
                    </ul>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* Ask area */}
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <input
              type="text"
              value={askText}
              onChange={(e) => setAskText(e.target.value)}
              placeholder="Ask a question..."
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #818cf8",
                background: "#181c2a",
                color: "#e3e6f0",
                fontSize: 16,
              }}
            />
            <Button variant="contained" onClick={handleAsk} sx={buttonStyle}>
              Ask
            </Button>
          </div>

          {/* Previous questions */}
          <div style={{ marginBottom: 16 }}>
            {askArray.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#818cf8", mb: 1 }}
                >
                  Previous Questions:
                </Typography>
                <div
                  style={{
                    resize: "vertical",
                    overflow: "auto",
                    minHeight: 48,
                    maxHeight: 240,
                    background: "#181c2a",
                    borderRadius: 8,
                    border: "1px solid #818cf8",
                    width: "100%",
                  }}
                >
                  <ul style={{ margin: 0, padding: 0, listStyle: "disc" }}>
                    {askArray.map((q, i) => (
                      <li
                        key={i}
                        style={{
                          color: "#e3e6f0",
                          marginBottom: 4,
                          paddingLeft: 20,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ flex: 1 }}>{q}</span>
                        <Button
                          size="small"
                          onClick={() => handleRemovePrompt(i)}
                          sx={{
                            ...buttonStyle,
                            bgcolor: "#be185d",
                            ":hover": { bgcolor: "#a21caf" },
                            minWidth: 0,
                            px: 2,
                            fontSize: 14,
                          }}
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>

          {/* AI response */}
          <div>
            <Typography
              variant="subtitle2"
              sx={{ color: "#818cf8", mb: 1 }}
            >
              AI Response:
            </Typography>
            <div
              style={{
                resize: "vertical",
                overflow: "auto",
                minHeight: 48,
                maxHeight: 240,
                background: "#181c2a",
                borderRadius: 8,
                border: "1px solid #818cf8",
                width: "100%",
                padding: "16px",
                color: "#e3e6f0",
                fontSize: 16,
              }}
            >
              {aiResponse}
            </div>
          </div>
        </Paper>
      )}

      {error && <Typography color="error">{error}</Typography>}

      {/* Devices table */}
      {devices && devices.devices && (
        <Paper
          className="glass-card glow"
          sx={{
            p: 3,
            mt: 4,
            mb: 2,
            borderRadius: 3,
            boxShadow: 1,
            width: "80%",
            mx: "auto",
            background: "#23263a",
            color: "#e3e6f0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 700, color: "#fff" }}
          >
            MQTT Devices
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              mx: "auto",
              background: "#23263a",
              color: "#e3e6f0",
              boxShadow: 3,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ background: "#181c2a" }}>
                  <TableCell sx={{ color: "#60a5fa", fontWeight: 600 }}>
                    Device ID
                  </TableCell>
                  <TableCell sx={{ color: "#60a5fa", fontWeight: 600 }}>
                    Reading Count
                  </TableCell>
                  <TableCell sx={{ color: "#60a5fa", fontWeight: 600 }}>
                    First Reading
                  </TableCell>
                  <TableCell sx={{ color: "#60a5fa", fontWeight: 600 }}>
                    Last Reading
                  </TableCell>
                  <TableCell sx={{ color: "#60a5fa", fontWeight: 600 }}>
                    Avg Power (kW)
                  </TableCell>
                  <TableCell sx={{ color: "#60a5fa", fontWeight: 600 }}>
                    Avg Voltage (V)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.devices.map((dev) => {
                  // LED logic: green flashing if last_reading <30s, else dark red steady
                  let ledColor = "#22c55e"; // green
                  let ledFlash = false;
                  let lastReadingDate = dev.last_reading ? new Date(dev.last_reading) : null;
                  let now = new Date();
                  let diffSec = lastReadingDate ? (now - lastReadingDate) / 1000 : null;
                  if (diffSec == null || diffSec > 90) {
                    ledColor = "#991b1b"; // dark red
                    ledFlash = false;
                  } else {
                    ledFlash = true;
                  }
                  return (
                    <TableRow
                      key={dev.device_id}
                      sx={{ background: "#23263a" }}
                    >
                      <TableCell sx={{ color: "#e3e6f0", display: "flex", alignItems: "center", gap: 1 }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            marginRight: 8,
                            background: ledColor,
                            boxShadow: ledFlash ? `0 0 8px 2px ${ledColor}` : undefined,
                            animation: ledFlash ? "led-flash 1s infinite alternate" : undefined,
                            border: ledFlash ? "2px solid #bbf7d0" : "2px solid #991b1b",
                            transition: "background 0.2s, box-shadow 0.2s"
                          }}
                        />
                        {dev.device_id}
                      </TableCell>
                      <TableCell sx={{ color: "#e3e6f0" }}>
                        {dev.reading_count}
                      </TableCell>
                      <TableCell sx={{ color: "#e3e6f0" }}>
                        {dev.first_reading}
                      </TableCell>
                      <TableCell sx={{ color: "#e3e6f0" }}>
                        {dev.last_reading}
                      </TableCell>
                      <TableCell sx={{ color: "#e3e6f0" }}>
                        {dev.avg_power}
                      </TableCell>
                      <TableCell sx={{ color: "#e3e6f0" }}>
                        {dev.avg_voltage}
                      </TableCell>
                    </TableRow>
                  );
                })}

              </TableBody>
            </Table>
            <Typography
              sx={{ mt: 2, color: "#60a5fa", fontWeight: 600 }}
            >
              Total Devices: {devices.total_devices}
            </Typography>
          </TableContainer>
        </Paper>
      )}

      {/* Readings table */}
      {readings && readings.latest_readings && (
        <Paper
          className="glass-card glow"
          sx={{
            p: 3,
            mt: 4,
            mb: 2,
            borderRadius: 3,
            boxShadow: 1,
            width: "80%",
            mx: "auto",
            background: "#23263a",
            color: "#e3e6f0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 700, color: "#fff" }}
          >
            Latest Device Readings
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "#181c2a" }}>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Device ID
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Timestamp
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Power
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Voltage
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Current
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Import kWh
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Export kWh
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Frequency
                  </TableCell>
                  <TableCell sx={{ color: "#38bdf8", fontWeight: 600 }}>
                    Power Factor
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {readings.latest_readings.map((reading, idx) => (
                  <TableRow key={idx} sx={{ background: "#23263a" }}>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {reading.device_id}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {reading.timestamp}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {typeof reading.power === "number"
                        ? reading.power.toFixed(5)
                        : reading.power}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {typeof reading.voltage === "number"
                        ? reading.voltage.toFixed(5)
                        : reading.voltage}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {typeof reading.current === "number"
                        ? reading.current.toFixed(5)
                        : reading.current}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {typeof reading.import_kwh === "number"
                        ? reading.import_kwh.toFixed(5)
                        : reading.import_kwh}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {typeof reading.export_kwh === "number"
                        ? reading.export_kwh.toFixed(5)
                        : reading.export_kwh}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {typeof reading.frequency === "number"
                        ? reading.frequency.toFixed(5)
                        : reading.frequency}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {typeof reading.power_factor === "number"
                        ? reading.power_factor.toFixed(5)
                        : reading.power_factor}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            sx={{ mt: 2, color: "#38bdf8", fontWeight: 600 }}
          >
            Device Count: {readings.device_count}
          </Typography>
        </Paper>
      )}

      {/* Topics table */}
      {topics && topics.topics && (
        <Paper
          className="glass-card glow"
          sx={{
            p: 3,
            mt: 4,
            mb: 2,
            borderRadius: 3,
            boxShadow: 1,
            width: "80%",
            mx: "auto",
            background: "#23263a",
            color: "#e3e6f0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 700, color: "#fff" }}
          >
            MQTT Topics
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "#181c2a" }}>
                  <TableCell sx={{ color: "#f472b6", fontWeight: 600 }}>
                    Topic
                  </TableCell>
                  <TableCell sx={{ color: "#f472b6", fontWeight: 600 }}>
                    Message Count
                  </TableCell>
                  <TableCell sx={{ color: "#f472b6", fontWeight: 600 }}>
                    First Message
                  </TableCell>
                  <TableCell sx={{ color: "#f472b6", fontWeight: 600 }}>
                    Last Message
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topics.topics.map((topic, idx) => (
                  <TableRow key={idx} sx={{ background: "#23263a" }}>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {topic.topic}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {topic.message_count}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {topic.first_message}
                    </TableCell>
                    <TableCell sx={{ color: "#e3e6f0" }}>
                      {topic.last_message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            sx={{ mt: 2, color: "#f472b6", fontWeight: 600 }}
          >
            Total Topics: {topics.total_topics}
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export default Dashboard;
